<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { T } from '@tolgee/vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import Collect4xImage from '@/assets/images/prize-cards/collect4x.png'
import Collect20xImage from '@/assets/images/prize-cards/collect20x.png'
import { computed, ref, watch } from 'vue'
import { AD_TYPE, useAd } from '@/composables/game/ad'
import { useConfig } from '@/composables/game/config'
import { useEcommercePrice } from '@/composables/game/ecommerce'
import PrizeCard from '@/components/PrizeCard.vue'
import { CARD_COLORS } from '@/enums/common'
import StrokeCoinImage from '@/assets/images/stroke-coin.png'
import { type EcommerceItem, SPECIAL_ECOMMERCE_ITEM_ID } from '@/types/shared/player-config'
import ShopItemDetails from '@/components/ShopItemDetails.vue'
import api from '@/services/api'
import { env } from '@/env'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'

const props = defineProps<{
	show: boolean
	profit: number
	offlineProfitHours: number
}>()
const emit = defineEmits(['hide'])

const { addHapticFeedback } = useHapticFeedback()
const {
  showAd,
  watchedDailyAdsCount,
  maxDailyAdsCount,
  adWatchedOfflineProfitMultiplier,
} = useAd(AD_TYPE.FOR_OFFLINE_PROFIT)
const {
  maxDailyAdsCount: forSpinMaxDailyAdsCount,
  watchedDailyAdsCount: forSpinWatchedDailyAdsCount,
} = useAd(AD_TYPE.FOR_SPIN)
const { config } = useConfig()
const { getStarPrice } = useEcommercePrice()

const allAdsWatched = computed(() => watchedDailyAdsCount.value >= maxDailyAdsCount.value || forSpinWatchedDailyAdsCount.value >= forSpinMaxDailyAdsCount.value)
const ecommerceItemX4 = computed(() => {
  const mustItem = config.value.game.ecommerce.items.find(item => item.id === SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X4)
  if (!mustItem) {
    api.reportError({
      code: 'ecommerceItemNotFound',
      description: `Ecommerce item with the id of ${SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X4} not found in backend list`,
      metadata: {},
    })
    throw new Error('Ecommerce item not found')
  }
  return mustItem
})
const ecommerceItemX20 = computed(() => {
  const mustItem = config.value.game.ecommerce.items.find(item => item.id === SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X20)
  if (!mustItem) {
    api.reportError({
      code: 'ecommerceItemNotFound',
      description: `Ecommerce item with the id of ${SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X20} not found in backend list`,
      metadata: {},
    })
    throw new Error('Ecommerce item not found')
  }
  return mustItem
})

const selectedEcommerceItem = ref<EcommerceItem | null>(null)

const usdAmountX4 = computed(() => getStarPrice(ecommerceItemX4.value).usdAmount)
const multipliedProfitX4 = computed(() => props.profit * 4)

const usdAmountX20 = computed(() => getStarPrice(ecommerceItemX20.value).usdAmount)
const multipliedProfitX20 = computed(() => props.profit * 20)

const ecommerceImageMap: Record<string, string> = {
  [SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X4]: Collect4xImage,
  [SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X20]: Collect20xImage,
}

const canShowCards = computed(() => props.offlineProfitHours >= (env.app.isProd ? 2 : 0.01))
const loadingWatchAd = ref<boolean>(false)

async function onWatchAd() {
  loadingWatchAd.value = true
  const watched = await showAd()
  loadingWatchAd.value = false
  if (watched) {
    emit('hide')
  }
}

watch(() => props.show, show => {
  if (show) {
    selectedEcommerceItem.value = null
  }
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    :back-button="!!selectedEcommerceItem"
    @hide="$emit('hide')"
    @back="selectedEcommerceItem = null"
  >
    <ShopItemDetails
      v-if="selectedEcommerceItem"
      :image="ecommerceImageMap[selectedEcommerceItem.id]"
      :item="selectedEcommerceItem"
      :coins-to-earn="selectedEcommerceItem.id === SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X4 ? multipliedProfitX4 : multipliedProfitX20"
      @hide="$emit('hide')"
    />
    <div
      v-else
      class="w-auto flex flex-col items-center relative"
    >
      <CoinIcon class="w-[77px] h-[120px] text-white" />
      <NumberFormatTooltip
        :number="profit"
        :formatting-type="'full'"
      >
        <div
          class="mt-4 text-[32px] leading-[40px] font-bold text-light-white"
        >
          {{ formatNumberFullTruncate(profit) }}
        </div>
      </NumberFormatTooltip>
      <div class="max-w-[214px] text-center text-base font-medium text-white/60">
        <T
          key-name="offline_profit.message"
          default-value="You have collected profit while you were away!"
        />
      </div>
      <div
        v-if="canShowCards"
        class="grid grid-cols-2 justify-between gap-3 mt-8 w-full"
      >
        <PrizeCard
          :color="CARD_COLORS.BLUE"
          @click="selectedEcommerceItem = ecommerceItemX4"
        >
          <NumberFormatTooltip
            :number="multipliedProfitX4"
            :formatting-type="'full'"
          >
            <div
              class="flex gap-1 items-center mt-6"
            >
              <img
                :src="StrokeCoinImage"
                class="h-6 w-auto object-contain"
              >
              <span
                class="text-[24px] leading-[18px] font-[900] text-white black-stroke disable-slashed-zero overflow-visible"
              >
                {{
                  formatNumberFullTruncate(multipliedProfitX4)
                }}</span>
            </div>
          </NumberFormatTooltip>
          <div class="w-[100px] mt-5 flex-1">
            <img
              :src="Collect4xImage"
              class="w-full h-auto"
            >
          </div>
          <div
            class="w-full flex rounded-[13px] p-0.5 green-stroke-gradient"
          >
            <BaseButton
              style-type="primary"
              button-class="px-3 h-[48px]"
              class="w-full"
            >
              ${{ formatNumberFullTruncate(usdAmountX4, true) }}
            </BaseButton>
          </div>
        </PrizeCard>

        <PrizeCard
          :color="CARD_COLORS.ORANGE"
          @click="selectedEcommerceItem = ecommerceItemX20"
        >
          <NumberFormatTooltip
            :number="multipliedProfitX20"
            :formatting-type="'full'"
          >
            <div
              class="flex gap-1 items-center mt-6"
            >
              <img
                :src="StrokeCoinImage"
                class="h-6 w-auto object-contain"
              >
              <span
                class="text-[24px] leading-[18px] font-[900] text-white black-stroke disable-slashed-zero overflow-visible"
              >
                {{
                  formatNumberFullTruncate(multipliedProfitX20)
                }}</span>
            </div>
          </NumberFormatTooltip>
          <div class="w-[134px] mt-4 flex-1">
            <img
              :src="Collect20xImage"
              class="w-full h-auto"
            >
          </div>
          <div
            class="w-full flex rounded-[13px] gold-gradient"
          >
            <div
              class="w-full flex rounded-[13px] p-0.5 white-stroke-gradient"
            >
              <BaseButton
                style-type="gold"
                button-class="px-3 h-[48px]"
                class="w-full"
              >
                ${{ formatNumberFullTruncate(usdAmountX20, true) }}
              </BaseButton>
            </div>
          </div>
        </PrizeCard>
      </div>
      <BaseButton
        :style-type="canShowCards ? 'tertiary' : 'primary' "
        class="w-full mt-3"
        @click="addHapticFeedback(); $emit('hide');"
      >
        <template #leftIcon>
          <CoinIcon class="w-auto h-full" />
        </template>
        <T
          key-name="offline_profit.claim"
          default-value="Claim"
        />
      </BaseButton>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

.second-black-stroke {
	-webkit-text-stroke: 1.6px black;
}

.black-stroke {
	text-shadow: -1px -1px 0 #000,
	1px -1px 0 #000,
	-1px 1px 0 #000,
	1px 1px 0 #000,
	-1.6px 0 0 #000,
	1.6px 0 0 #000,
	0 -1.6px 0 #000,
	0 1.6px 0 #000,
	-0.8px -0.8px 0 #000, /* Finer shadow adjustments */ 0.8px -0.8px 0 #000,
	-0.8px 0.8px 0 #000,
	0.8px 0.8px 0 #000;
}

.icon-stroke {
	stroke: black !important; /* Set the stroke color */
	stroke-width: 1.6px !important; /* Set the stroke thickness */
	stroke-linejoin: round !important; /* Optional: Round the corners of the stroke */
	fill: currentColor !important; /* Keep the fill color of the icon */
}

.green-stroke-gradient {
	background: linear-gradient(to right, rgba(126, 187, 0, 0.75) 0%, rgba(187, 238, 83, 0.75) 33%);
}

.gold-gradient {
	background: linear-gradient(to right,
	#FFF246 0%,
	#E5B80B 37%,
	#FFF571 69%,
	#E5B80B 100%
	);
}

.white-stroke-gradient {
	background: linear-gradient(to right,
	rgba(255, 255, 255, 0.38) 0%,
	rgba(255, 255, 255, 0) 100%
	);
}

</style>
